import React, { Component } from 'react';
import {
    Box,
    Container,
    Card,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    withStyles,
    CircularProgress,
    TextField
} from '@material-ui/core';
import Swal from 'sweetalert2';
import { withSnackbar } from 'notistack';
import AdminLayout from '../components/adminLayout';
import CustomButton from '../../shared/components/common/customButton';
import { withRouter } from 'react-router-dom';
import { AdminHomeService } from './service';
import ClientsModal from '../../shared/components/common/clientsModal';
import QuickCompanyModal from '../../workshop/components/QuickCompanyModal';
import { LoggedUserService } from '../../../loggedUser/loggedUserService';

const ADMIN_HOME_STATUSES = [
    'aguardando estimativa',
    'estimativa entregue',
    'aguardando equipamento',
    'equipamento em analise',
    'aguardando orçamento',
    'pendente de aprovação',
    'aprovado para manutencao',
    'aguardando inspeção e laudo',
    'aguardando faturamento',
    'pronto para retirada',
    'enviado',
    'autorizar manutencao',
    'finalizado'
];

function normalizeStatus(value) {
    return String(value || '').trim().toLowerCase();
}

function createDefaultCounters() {
    return ADMIN_HOME_STATUSES.reduce((acc, status) => {
        acc[status] = 0;
        return acc;
    }, {});
}

function normalizeDigits(value) {
    return String(value || '').replace(/\D/g, '');
}

function normalizeFileNameWithoutExtension(value) {
    const normalizedValue = String(value || '')
        .trim()
        .split(/[\\/]/)
        .pop()
        .toLowerCase();

    return normalizedValue.replace(/\.[^./\\]+$/, '');
}

function getNotaRemessaFileName(item = {}) {
    return item.notaRemessaFileName
        || item.nota_remessa_file_name
        || item.notaRemessaFile
        || item.nota_remessa
        || '';
}

function getBudgetFileName(item = {}) {
    return item.budgetFileName
        || item.budget_file_name
        || item.orcamento_file_name
        || item.orcamentoFileName
        || item.nome_arquivo_orcamento
        || '';
}

function getFileNamesForSearch(item = {}) {
    const names = [];
    const directLists = [item.serviceFileNames, item.service_file_names];
    const objectLists = [item.serviceFiles, item.service_files];

    directLists.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((name) => {
            if (typeof name === 'string' && name.trim()) {
                names.push(name.trim());
            }
        });
    });

    objectLists.forEach((list) => {
        if (!Array.isArray(list)) return;
        list.forEach((file) => {
            if (file && typeof file === 'object') {
                const type = String(file.type || file.fileType || '').toLowerCase();
                if (type !== 'nota_remessa' && type !== 'nota remessa') return;
                const name = file.fileName || file.file_name || file.nome || file.name;
                if (typeof name === 'string' && name.trim()) {
                    names.push(name.trim());
                }
            }
        });
    });

    const fallbackNames = [getBudgetFileName(item), getNotaRemessaFileName(item)].filter(Boolean);
    fallbackNames.forEach((name) => names.push(name));

    return Array.from(new Set(names));
}

function matchesGlobalSearch(item, searchTerm) {
    const query = String(searchTerm || '').trim();
    if (!query) return true;

    const queryDigits = normalizeDigits(query);
    const queryNote = normalizeFileNameWithoutExtension(query);

    const cnpjDigits = normalizeDigits(item?.cnpj);
    const noteNames = getFileNamesForSearch(item)
        .map((name) => normalizeFileNameWithoutExtension(name))
        .filter(Boolean);

    const cnpjMatches = queryDigits.length > 0 && cnpjDigits.includes(queryDigits);
    const notaMatches = queryNote.length > 0 && noteNames.some((name) => name.includes(queryNote));

    return cnpjMatches || notaMatches;
}

const styles = theme => ({
    root: {
        paddingTop: '2rem',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        margin: '0 auto',
        backgroundColor: '#F4F6F8',
        minHeight: '85vh',
    },
    cardsWrapper: {
        display: 'flex',
        gap: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '2rem',
    },
    statusCard: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '16px',
        width: '200px',
        height: '135px',
        boxSizing: 'border-box',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
        }
    },
    statusCounter: {
        fontWeight: 'bold',
        fontSize: '2.5rem',
        color: '#111827',
        lineHeight: 1
    },
    statusBadge: {
        fontSize: '11px',
        lineHeight: 1.2,
        padding: '6px 10px',
        color: '#ffffff',
        fontWeight: '700',
        marginTop: '12px',
        textTransform: 'uppercase',
        borderRadius: '20px',
        wordWrap: 'break-word',
        maxWidth: '100%',
    },
    panelCard: {
        backgroundColor: '#FFFFFF',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #E5E7EB',
        position: 'relative',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        '&:MuiTypography-root': {
            color: '#111827',
        }
    },
    panelTitle: {
        fontSize: '1.25rem',
        fontWeight: '600',
        color: '#111827',
        marginBottom: '20px',
        textAlign: 'center',
    },
    tableWrapper: {
        overflowX: 'auto',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    },
    tableTransparent: {
        backgroundColor: '#FFFFFF',
        '& .MuiTableCell-root': {
            borderBottom: '1px solid #E5E7EB',
            color: '#374151',
            fontSize: '0.875rem',
            padding: '16px',
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
            backgroundColor: '#F9FAFB',
            color: '#6B7280',
            fontWeight: '600',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            padding: '12px 16px',
            borderBottom: '1px solid #E5E7EB',
        },
        '& .MuiTableRow-root:hover': {
            backgroundColor: '#F9FAFB'
        },
        '& a': {
            fontWeight: '600',
            color: '#2563EB !important',
            textDecoration: 'none',
        }
    },
    actionLink: {
        fontWeight: '600',
        color: '#2563EB !important',
        backgroundColor: '#EFF6FF',
        padding: '6px 12px',
        borderRadius: '6px',
        textDecoration: 'none',
        cursor: 'pointer',
        display: 'inline-block',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#DBEAFE',
            color: '#1D4ED8 !important',
        }
    },
    panelDescription: {
        marginTop: '.5rem',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '.25rem',
    },
    descriptionText: {
        color: '#212529',
    },
    descriptionBadge: {
        fontSize: '0.75rem',
        padding: '4.2px 7.8px',
        borderRadius: '0.375rem',
        color: '#FFFFFF',
        lineHeight: 1,
    },
    buttonGroup: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '12px',
        marginBottom: '24px',
        padding: '16px 20px',
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        border: '1px solid #E5E7EB',
    },
    searchInputWrapper: {
        width: '100%',
        maxWidth: '340px',
        flex: '1 1 280px',
        [theme.breakpoints.down('xs')]: {
            maxWidth: '100%',
        },
    },
    searchField: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: '#F9FAFB',
            height: 38,
            '& fieldset': {
                borderColor: '#E5E7EB',
            },
            '&:hover fieldset': {
                borderColor: '#D1D5DB',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#2563EB',
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 12px',
            fontSize: '0.875rem',
            color: '#374151',
        },
        '& .MuiOutlinedInput-input::placeholder': {
            color: '#9CA3AF',
            opacity: 1,
        },
    },
    titleSection: {
        color: '#111827',
        marginBottom: '1rem',
        fontSize: '1.5rem',
        fontWeight: '600',
    },
    subtitleSection: {
        color: '#374151',
        fontSize: '1.1rem',
    },
    captionSection: {
        fontSize: '0.875rem',
        color: '#6B7280',
        fontWeight: '500',
        marginTop: '0.25rem',
    },
    tableFooter: {
        borderTop: '2px solid #E5E7EB',
        '& .MuiTableCell-root': {
            color: '#111827',
            fontWeight: '600',
            borderBottom: 'none !important',
        },
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        color: '#111827'
    },
    errorContainer: {
        backgroundColor: '#dc3545',
        color: '#ffffff',
        padding: theme.spacing(2),
        borderRadius: '4px',
        textAlign: 'center',
        marginBottom: theme.spacing(2)
    }
});

class AdminHomePage extends Component {
    constructor(props) {
        super(props);

        this.state = {
            HOME_Cotacoes: [],
            HOME_StatusAtual: null,
            contadores: createDefaultCounters(),
            dadosFiltrados: [],
            totalValor: 0,
            loading: true,
            error: null,
            lastUpdate: null,
            clientsModalOpen: false,
            registerClientModalOpen: false,
            searchTerm: '',
        };

        this.intervalId = null;
    }

    componentDidMount() {
        this.initHome();
        this.intervalId = setInterval(this.atualizarHome, 30000);
    }

    componentWillUnmount() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    initHome = async () => {
        try {
            this.setState({ loading: true, error: null });

            const homeData = await AdminHomeService.getHomeData();

            this.setState({
                HOME_Cotacoes: homeData.HOME_Cotacoes,
                contadores: { ...createDefaultCounters(), ...homeData.contadores },
                lastUpdate: homeData.lastUpdate,
                loading: false
            }, () => {
                if (homeData.HOME_Cotacoes.length > 0) {
                    const statusAtual = normalizeStatus(homeData.HOME_Cotacoes[0].status);
                    this.setState({ HOME_StatusAtual: statusAtual });
                    this.gerarRelatorioPorStatus(statusAtual);
                }
            });

        } catch (error) {
            console.error('Erro ao carregar dados da Home:', error);
            const { currentLanguage } = this.props;
            this.setState({
                error: currentLanguage.bepiService.admin.admin_home_error_loading_details,
                loading: false
            });
            this.loadMockData();
        }
    }

    loadMockData = () => {
        const mockData = [];
        const { contadores, cotacoesFiltradas } = AdminHomeService.groupByStatus(mockData);

        this.setState({
            HOME_Cotacoes: cotacoesFiltradas,
            contadores,
            lastUpdate: new Date().toISOString()
        }, () => {
            if (cotacoesFiltradas.length > 0) {
                const statusAtual = normalizeStatus(cotacoesFiltradas[0].status);
                this.setState({ HOME_StatusAtual: statusAtual });
                this.gerarRelatorioPorStatus(statusAtual);
            }
        });
    }

    filtrarStatus = async (status) => {
        try {
            this.setState({ loading: true });
            const dataByStatus = await AdminHomeService.getHomeDataByStatus(status);

            this.setState({
                HOME_StatusAtual: normalizeStatus(status),
                dadosFiltrados: dataByStatus.dadosFiltrados,
                totalValor: dataByStatus.totalValor,
                loading: false
            });

        } catch (error) {
            console.error('Erro ao filtrar por status:', error);
            this.gerarRelatorioPorStatus(status);
            this.setState({ loading: false });
        }
    }

    handleAssumirQuotation = async (item) => {
        try {
            // Get user from LoggedUserService
            const loggedUser = await LoggedUserService.getLoggedUser();
            if (!loggedUser || (!loggedUser.id && !loggedUser.user_id)) {
                Swal.fire('Erro', 'Usuário não logado.', 'error');
                return;
            }

            const result = await Swal.fire({
                title: 'Confirmar',
                text: 'Deseja assumir esta cotação?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sim',
                cancelButtonText: 'Não'
            });

            if (result.isConfirmed) {
                this.setState({ loading: true });
                const response = await AdminHomeService.assumeQuotation(item.id, loggedUser);

                if (response.success) {
                    Swal.fire('Sucesso', 'Cotação assumida com sucesso.', 'success');
                    // Refresh current data immediately without reloading
                    const updatedCotacoes = this.state.HOME_Cotacoes.map(c => {
                        if (c.id === item.id) {
                            return {
                                ...c,
                                assumed_by_user_id: loggedUser.id || loggedUser.user_id,
                                assumed_by_user_name: loggedUser.name || loggedUser.nome || loggedUser.userName
                            };
                        }
                        return c;
                    });

                    this.setState({ HOME_Cotacoes: updatedCotacoes }, () => {
                        if (this.state.HOME_StatusAtual) {
                            this.gerarRelatorioPorStatus(this.state.HOME_StatusAtual);
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Erro ao assumir:', error);
            Swal.fire('Erro', error?.response?.data?.error || 'Erro ao assumir cotação.', 'error');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleUnassignQuotation = async (item, type) => {
        try {
            const confirmText = type === 'workshop'
                ? `Deseja remover ${item.assumed_by_workshop_user_name} como técnico responsável?`
                : `Deseja remover ${item.assumed_by_user_name} como comercial responsável?`;

            const result = await Swal.fire({
                title: 'Confirmar',
                text: confirmText,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim, remover',
                cancelButtonText: 'Não'
            });

            if (result.isConfirmed) {
                this.setState({ loading: true });
                if (type === 'workshop') {
                    await AdminHomeService.unassignWorkshopQuotation(item.id);
                } else {
                    await AdminHomeService.unassignCommercialQuotation(item.id);
                }

                Swal.fire('Sucesso', 'Responsável removido com sucesso.', 'success');

                const clearItem = (c) => {
                    if (c.id === item.id) {
                        if (type === 'workshop') {
                            return { ...c, assumed_by_workshop_user_id: null, assumed_by_workshop_user_name: null, assumed_by_workshop_at: null };
                        } else {
                            return { ...c, assumed_by_user_id: null, assumed_by_user_name: null };
                        }
                    }
                    return c;
                };

                this.setState(prevState => ({
                    HOME_Cotacoes: prevState.HOME_Cotacoes.map(clearItem)
                }), () => {
                    if (this.state.HOME_StatusAtual) {
                        this.gerarRelatorioPorStatus(this.state.HOME_StatusAtual);
                    }
                });
            }
        } catch (error) {
            console.error('Erro ao remover responsável:', error);
            Swal.fire('Erro', error?.response?.data?.error || 'Erro ao remover responsável.', 'error');
        } finally {
            this.setState({ loading: false });
        }
    }

    handleMarcarComoEnviado = async (item) => {
        const { currentLanguage } = this.props;

        if (normalizeStatus(item.status) !== 'pronto para retirada') {
            return;
        }

        try {
            const result = await Swal.fire({
                title: currentLanguage.bepiService.admin.admin_home_mark_sent_confirm_title || 'Confirmar',
                text: currentLanguage.bepiService.admin.admin_home_mark_sent_confirm_text || 'Deseja avancar este item para Enviado?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sim',
                cancelButtonText: 'Nao'
            });

            if (!result.isConfirmed) {
                return;
            }

            this.setState({ loading: true });
            await AdminHomeService.updateQuotationStatus(item.id, 'enviado');
            await this.atualizarHome();

            Swal.fire(
                currentLanguage.bepiService.shared.shared_details_success_title || 'Sucesso',
                currentLanguage.bepiService.admin.admin_home_mark_sent_success || 'Status atualizado para Enviado.',
                'success'
            );
        } catch (error) {
            console.error('Erro ao atualizar status para enviado:', error);
            Swal.fire(
                currentLanguage.bepiService.shared.shared_details_error_label || 'Erro',
                error?.response?.data?.error || currentLanguage.bepiService.admin.admin_home_mark_sent_error || 'Nao foi possivel atualizar para Enviado.',
                'error'
            );
        } finally {
            this.setState({ loading: false });
        }
    }

    handleAvancarAutorizacaoManutencao = async (item) => {
        const { currentLanguage } = this.props;
        const currentStatus = normalizeStatus(item.status);

        if (currentStatus !== 'autorizar manutencao' && !currentStatus.includes('pendente')) {
            return;
        }

        const isAuthorizationStep = currentStatus.includes('pendente');
        const nextStatus = isAuthorizationStep ? 'autorizar manutencao' : 'aprovado para manutencao';
        const confirmTitle = isAuthorizationStep
            ? currentLanguage.bepiService.admin.admin_home_authorize_maintenance_confirm_title
            : currentLanguage.bepiService.admin.admin_home_approve_maintenance_confirm_title;
        const confirmText = isAuthorizationStep
            ? currentLanguage.bepiService.admin.admin_home_authorize_maintenance_confirm_text
            : currentLanguage.bepiService.admin.admin_home_approve_maintenance_confirm_text;
        const successText = isAuthorizationStep
            ? currentLanguage.bepiService.admin.admin_home_authorize_maintenance_success
            : currentLanguage.bepiService.admin.admin_home_approve_maintenance_success;
        const errorText = isAuthorizationStep
            ? currentLanguage.bepiService.admin.admin_home_authorize_maintenance_error
            : currentLanguage.bepiService.admin.admin_home_approve_maintenance_error;

        try {
            const result = await Swal.fire({
                title: confirmTitle || 'Confirmar',
                text: confirmText || 'Deseja avancar este item?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sim',
                cancelButtonText: 'Nao'
            });

            if (!result.isConfirmed) {
                return;
            }

            this.setState({ loading: true });
            await AdminHomeService.updateQuotationStatus(item.id, nextStatus);
            await this.atualizarHome();

            Swal.fire(
                currentLanguage.bepiService.shared.shared_details_success_title || 'Sucesso',
                successText || 'Status atualizado com sucesso.',
                'success'
            );
        } catch (error) {
            console.error('Erro ao avancar status de autorizacao de manutencao:', error);
            Swal.fire(
                currentLanguage.bepiService.shared.shared_details_error_label || 'Erro',
                error?.response?.data?.error || errorText || 'Nao foi possivel atualizar o status.',
                'error'
            );
        } finally {
            this.setState({ loading: false });
        }
    }

    gerarRelatorioPorStatus = (status) => {
        const currentStatus = normalizeStatus(status);
        const filtrados = this.state.HOME_Cotacoes.filter((cotacao) => normalizeStatus(cotacao.status) === currentStatus);
        const totalValor = filtrados.reduce((sum, item) => sum + (Number(item.valor_resposta) || 0), 0);

        this.setState({
            HOME_StatusAtual: currentStatus,
            dadosFiltrados: filtrados,
            totalValor
        });
    }

    handleSearchChange = (event) => {
        this.setState({ searchTerm: event.target.value });
    }

    isGlobalSearchActive = () => this.state.searchTerm.trim().length > 0

    getDisplayedRows = () => {
        const { HOME_Cotacoes, dadosFiltrados, searchTerm } = this.state;

        if (!this.isGlobalSearchActive()) {
            return dadosFiltrados;
        }

        return HOME_Cotacoes.filter((item) => matchesGlobalSearch(item, searchTerm));
    }

    hasNotaRemessaFieldAvailable = () =>
        this.state.HOME_Cotacoes.some((item) => item.hasNotaRemessaField)

    buscar_equipamento = async () => {
        const { currentLanguage } = this.props;
        const { value: equipamentoId, isConfirmed } = await Swal.fire({
            title: currentLanguage.bepiService.admin.admin_home_swal_search_title,
            input: 'text',
            inputLabel: currentLanguage.bepiService.admin.admin_home_swal_search_label,
            showCancelButton: true,
            confirmButtonText: currentLanguage.bepiService.admin.admin_home_swal_search_btn_confirm,
            cancelButtonText: currentLanguage.bepiService.admin.admin_home_swal_search_btn_cancel
        });

        if (!isConfirmed || !equipamentoId) return;

        try {
            const equipment = await AdminHomeService.searchEquipment(equipamentoId);
            Swal.fire({
                title: currentLanguage.bepiService.admin.admin_home_swal_found_title,
                html: `
                    <strong>Nome:</strong> ${equipment.nome}<br>
                    <strong>Marca:</strong> ${equipment.marca}<br>
                    <strong>Modelo:</strong> ${equipment.modelo}<br>
                    <strong>Nº Série:</strong> ${equipment.numero_serie}<br>
                    <strong>Cliente:</strong> ${equipment.cliente}
                `,
                icon: 'success'
            });
        } catch (err) {
            console.error(err);
            Swal.fire(currentLanguage.bepiService.admin.admin_home_swal_not_found_title, '', 'warning');
        }
    }

    atualizarHome = async () => {
        try {
            const homeData = await AdminHomeService.refreshHomeData();

            this.setState({
                HOME_Cotacoes: homeData.HOME_Cotacoes,
                contadores: { ...createDefaultCounters(), ...homeData.contadores },
                lastUpdate: homeData.lastUpdate
            }, () => {
                if (this.state.HOME_StatusAtual) {
                    this.gerarRelatorioPorStatus(this.state.HOME_StatusAtual);
                }
            });

        } catch (err) {
            console.error('Falha no refresh:', err);
        }
    }

    formatarData = (data) => AdminHomeService.formatarData(data)

    formatarMoeda = (valor) => AdminHomeService.formatarMoeda(valor)

    getCounter = (status) => {
        const parsed = Number(this.state.contadores?.[status]);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    getCardClass = (status) => {
        const { classes } = this.props;

        const statusAdministrativo = [
            'aguardando estimativa',
            'aguardando orçamento',
            'aguardando faturamento',
            'finalizado'
        ];

        const statusCliente = [
            'estimativa entregue',
            'aguardando equipamento',
            'pendente de aprovação'
        ];

        const statusOficina = [
            'equipamento em analise',
            'autorizar manutencao',
            'aprovado para manutencao',
            'aguardando inspeção e laudo',
            'aprovado para manutenção',
            'pronto para retirada',
            'enviado'
        ];

        if (statusAdministrativo.includes(status)) return classes.cardAdministrativo;
        if (statusCliente.includes(status)) return classes.cardCliente;
        if (statusOficina.includes(status)) return classes.cardOficina;

        return classes.cardAdministrativo;
    }

    renderStatusCards = () => {
        const { classes, currentLanguage } = this.props;

        const getColorForStatus = (status) => {
            const statusAdministrativo = ['aguardando estimativa', 'aguardando orçamento', 'aguardando faturamento', 'finalizado'];
            const statusCliente = ['estimativa entregue', 'pendente de aprovação'];
            if (statusAdministrativo.includes(status)) return '#04476c';
            if (statusCliente.includes(status)) return '#f8792a';
            return '#046c2c';
        };

        const statusLabels = {
            'aguardando estimativa': currentLanguage.bepiService.admin.admin_home_card_label_waiting_estimate,
            'estimativa entregue': currentLanguage.bepiService.admin.admin_home_card_label_estimate_delivered,
            'autorizar manutencao': currentLanguage.bepiService.admin.admin_home_card_label_authorize_maintenance,
            'aguardando equipamento': currentLanguage.bepiService.admin.admin_home_card_label_waiting_equipment,
            'equipamento em analise': currentLanguage.bepiService.admin.admin_home_card_label_equipment_analysis,
            'aguardando orçamento': currentLanguage.bepiService.admin.admin_home_card_label_waiting_budget,
            'pendente de aprovação': currentLanguage.bepiService.admin.admin_home_card_label_pending_approval,
            'aprovado para manutencao': currentLanguage.bepiService.admin.admin_home_card_label_approved_maintenance,
            'aguardando inspeção e laudo': currentLanguage.bepiService.admin.admin_home_card_label_waiting_inspection,
            'aguardando faturamento': currentLanguage.bepiService.admin.admin_home_card_label_waiting_nf,
            'pronto para retirada': currentLanguage.bepiService.admin.admin_home_card_label_ready_pickup,
            'enviado': currentLanguage.bepiService.admin.admin_home_card_label_sent,
            'finalizado': currentLanguage.bepiService.admin.admin_home_card_label_finalized,
        };

        const orderedStatuses = [
            'aguardando estimativa',
            'estimativa entregue',
            'aguardando equipamento',
            'equipamento em analise',
            'aguardando orçamento',
            'pendente de aprovação',
            'autorizar manutencao',
            'aprovado para manutencao',
            'aguardando inspeção e laudo',
            'aguardando faturamento',
            'pronto para retirada',
            'enviado',
            'finalizado'
        ];

        return (
            <Box className={classes.cardsWrapper}>
                {orderedStatuses.map((status) => {
                    const cor = getColorForStatus(status);
                    return (
                        <Box key={status}>
                            <Box
                                className={classes.statusCard}
                                onClick={() => this.filtrarStatus(status)}
                                style={{ borderLeft: `8px solid ${cor}` }}
                            >
                                <Typography className={classes.statusCounter}>
                                    {this.getCounter(status)}
                                </Typography>
                                <Typography variant="body2" className={classes.statusBadge} style={{ backgroundColor: cor }}>
                                    {(statusLabels[status] || status).split('\n').map((line, i, arr) => (
                                        <span key={`${status}-${i}`}>
                                            {line}
                                            {i < arr.length - 1 && <br />}
                                        </span>
                                    ))}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    }

    renderTabela = () => {
        const { HOME_StatusAtual, totalValor, loading, lastUpdate, searchTerm } = this.state;
        const { classes, currentLanguage } = this.props;
        const isGlobalSearchActive = this.isGlobalSearchActive();
        const displayedRows = this.getDisplayedRows();
        const totalValorExibido = isGlobalSearchActive
            ? displayedRows.reduce((sum, item) => sum + (Number(item.valor_resposta) || 0), 0)
            : totalValor;
        const statusHeaderLabel = currentLanguage.bepiService?.shared?.shared_quotation_table_status || 'Status';
        const noDataText = currentLanguage.bepiService?.workshop?.workshop_home_no_data_msg || 'Nenhum dado encontrado para este filtro';

        if (loading) {
            return (
                <Box className={classes.loadingContainer}>
                    <CircularProgress style={{ color: '#ffffff' }} />
                    <Typography style={{ marginLeft: '1rem', color: '#ffffff' }}>
                        {currentLanguage.bepiService.admin.admin_home_loading_text}
                    </Typography>
                </Box>
            );
        }

        const STATUS_DESCRICOES = {
            'aguardando estimativa': currentLanguage.bepiService.admin.admin_home_status_desc_waiting_estimate,
            'estimativa entregue': currentLanguage.bepiService.admin.admin_home_status_desc_estimate_delivered,
            'autorizar manutencao': currentLanguage.bepiService.admin.admin_home_status_desc_authorize_maintenance,
            'aguardando equipamento': currentLanguage.bepiService.admin.admin_home_status_desc_waiting_equipment,
            'equipamento em analise': currentLanguage.bepiService.admin.admin_home_status_desc_equipment_analysis,
            'aguardando orçamento': currentLanguage.bepiService.admin.admin_home_status_desc_waiting_budget,
            'pendente de aprovação': currentLanguage.bepiService.admin.admin_home_status_desc_pending_approval,
            'aprovado para manutencao': currentLanguage.bepiService.admin.admin_home_status_desc_approved_maintenance,
            'aguardando inspeção e laudo': currentLanguage.bepiService.admin.admin_home_status_desc_waiting_inspection,
            'aguardando faturamento': currentLanguage.bepiService.admin.admin_home_status_desc_waiting_nf,
            'pronto para retirada': currentLanguage.bepiService.admin.admin_home_status_desc_ready_pickup,
            'enviado': currentLanguage.bepiService.admin.admin_home_status_desc_sent,
            'finalizado': currentLanguage.bepiService.admin.admin_home_status_desc_finalized,
        };

        return (
            <Box>
                <Typography variant="h5" className={classes.titleSection}>
                    {isGlobalSearchActive
                        ? `${(currentLanguage.bepiService.admin.admin_home_btn_search || 'Pesquisar').toUpperCase()} (${searchTerm})`
                        : (HOME_StatusAtual ? HOME_StatusAtual.toUpperCase() : '')}
                    <br />
                    {!isGlobalSearchActive && (
                        <Typography className={classes.subtitleSection}>
                            {STATUS_DESCRICOES[HOME_StatusAtual] || ''}
                        </Typography>
                    )}
                    <Typography className={classes.captionSection}>
                        {currentLanguage.bepiService.admin.admin_home_last_update_text} {lastUpdate ?
                            new Date(lastUpdate).toLocaleTimeString('pt-BR') :
                            new Date().toLocaleTimeString('pt-BR')}
                    </Typography>
                </Typography>

                <Paper className={classes.tableWrapper}>
                    <Table className={classes.tableTransparent} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{currentLanguage.bepiService.admin.admin_home_table_header_created}</TableCell>
                                <TableCell>{currentLanguage.bepiService.admin.admin_home_table_header_code}</TableCell>
                                <TableCell>{currentLanguage.bepiService.admin.admin_home_table_header_cnpj}</TableCell>
                                <TableCell>{currentLanguage.bepiService.admin.admin_home_table_header_client}</TableCell>
                                {isGlobalSearchActive && <TableCell>{statusHeaderLabel}</TableCell>}
                                <TableCell align="right">{currentLanguage.bepiService.admin.admin_home_table_header_value}</TableCell>
                                <TableCell align="center">Técnico Resp.</TableCell>
                                <TableCell align="center">Comercial Resp.</TableCell>
                                <TableCell align="center">Nº Nota Remessa</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedRows.length > 0 ? displayedRows.map((item, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>{this.formatarData(item.data)}</TableCell>
                                    <TableCell>
                                        <Typography
                                            className={classes.actionLink}
                                            onClick={() => {
                                                if (item.tipo && item.tipo.toLowerCase() !== 'estimativa') {
                                                    this.props.history.push('/bepi/admin/quote');
                                                } else {
                                                    const cotacaoSelecionada = this.state.HOME_Cotacoes.find((cotacao) =>
                                                        cotacao.id === item.id && cotacao.tipo === item.tipo
                                                    );
                                                    if (cotacaoSelecionada) {
                                                        localStorage.setItem('cotacaoSelecionada', JSON.stringify(cotacaoSelecionada));
                                                        localStorage.setItem('clienteInfo', JSON.stringify({ nome: item.cliente, user_id: item.user_id }));
                                                    }
                                                    this.props.history.push(`/bepi/admin/quote/details?id=${item.id}`);
                                                }
                                            }}
                                        >
                                            {item.id}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            className={classes.actionLink}
                                            style={{ cursor: 'default' }}>
                                            {item.cnpj}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{item.cliente}</TableCell>
                                    {isGlobalSearchActive && <TableCell>{item.status || '-'}</TableCell>}
                                    <TableCell align="right">{this.formatarMoeda(item.valor_resposta)}</TableCell>
                                    <TableCell align="center">
                                        {item.assumed_by_workshop_user_name ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                                                    {item.assumed_by_workshop_user_name}
                                                </span>
                                                {(normalizeStatus(item.status) !== 'finalizado' && normalizeStatus(item.status) !== 'concluido') && (
                                                    <CustomButton
                                                        variant="redOutlined"
                                                        size="small"
                                                        onClick={() => this.handleUnassignQuotation(item, 'workshop')}
                                                    >
                                                        Devolver
                                                    </CustomButton>
                                                )}
                                            </div>
                                        ) : ''}
                                    </TableCell>
                                    <TableCell align="center">
                                        {item.assumed_by_user_id ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                                                    {item.assumed_by_user_name}
                                                </span>
                                                {(normalizeStatus(item.status) !== 'finalizado' && normalizeStatus(item.status) !== 'concluido') && (
                                                    <CustomButton
                                                        variant="redOutlined"
                                                        size="small"
                                                        onClick={() => this.handleUnassignQuotation(item, 'commercial')}
                                                    >
                                                        Devolver
                                                    </CustomButton>
                                                )}
                                            </div>
                                        ) : (
                                            (normalizeStatus(item.status) !== 'finalizado' && normalizeStatus(item.status) !== 'concluido') ? (
                                                <CustomButton
                                                    variant="darkBlueFilled"
                                                    size="small"
                                                    onClick={() => this.handleAssumirQuotation(item)}
                                                >
                                                    Assumir
                                                </CustomButton>
                                            ) : '-'
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <span style={{ fontSize: '0.85rem', color: '#374151' }}>
                                            {item.notaRemessaFileName ? normalizeFileNameWithoutExtension(item.notaRemessaFileName) : '-'}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={isGlobalSearchActive ? 9 : 8}
                                        align="center"
                                    >
                                        {noDataText}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                        <TableHead className={classes.tableFooter}>
                            <TableRow>
                                <TableCell
                                    colSpan={isGlobalSearchActive ? 7 : 6}
                                    align="right"
                                >
                                    {currentLanguage.bepiService.admin.admin_home_table_footer_total}
                                </TableCell>
                                <TableCell align="right">{this.formatarMoeda(totalValorExibido)}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </Paper>
            </Box>
        );
    }

    renderStatusCardsSafe = () => {
        const { classes, currentLanguage } = this.props;

        const getColorForStatus = (status) => {
            const statusAdministrativo = ['aguardando estimativa', 'aguardando orçamento', 'aguardando faturamento', 'finalizado'];
            const statusCliente = ['estimativa entregue', 'pendente de aprovação'];
            if (statusAdministrativo.includes(status)) return '#04476c';
            if (statusCliente.includes(status)) return '#f8792a';
            return '#046c2c';
        };

        const statusCards = [
            {
                status: 'aguardando estimativa',
                label: currentLanguage.bepiService.admin.admin_home_card_label_waiting_estimate || 'Aguardando\nEstimativa'
            },
            {
                status: 'estimativa entregue',
                label: currentLanguage.bepiService.admin.admin_home_card_label_estimate_delivered || 'Estimativa\nEntregue'
            },
            {
                status: 'aguardando equipamento',
                label: currentLanguage.bepiService.admin.admin_home_card_label_waiting_equipment || 'Aguardando\nEquipamento'
            },
            {
                status: 'equipamento em analise',
                label: currentLanguage.bepiService.admin.admin_home_card_label_equipment_analysis || 'Equipamento\nEm Analise'
            },
            {
                status: 'aguardando orçamento',
                label: currentLanguage.bepiService.admin.admin_home_card_label_waiting_budget || 'Aguardando\nOrcamento'
            },
            {
                status: 'pendente de aprovação',
                label: currentLanguage.bepiService.admin.admin_home_card_label_pending_approval || 'Pendente de\nAprovacao'
            },
            {
                status: 'autorizar manutencao',
                label: currentLanguage.bepiService.admin.admin_home_card_label_authorize_maintenance || 'Autorizar\nManutencao'
            },
            {
                status: 'aprovado para manutencao',
                label: currentLanguage.bepiService.admin.admin_home_card_label_approved_maintenance || 'Aprovado para\nManutencao'
            },
            {
                status: 'aguardando inspeção e laudo',
                label: currentLanguage.bepiService.admin.admin_home_card_label_waiting_inspection || 'Aguardando\nInspecao e Laudo'
            },
            {
                status: 'aguardando faturamento',
                label: currentLanguage.bepiService.admin.admin_home_card_label_waiting_nf || 'Aguardando\nFaturamento'
            },
            {
                status: 'pronto para retirada',
                label: currentLanguage.bepiService.admin.admin_home_card_label_ready_pickup || 'Pronto para\nRetirada'
            },
            {
                status: 'enviado',
                label: currentLanguage.bepiService.admin.admin_home_card_label_sent || 'Enviado'
            },
            {
                status: 'finalizado',
                label: currentLanguage.bepiService.admin.admin_home_card_label_finalized || 'Finalizado'
            }
        ];

        return (
            <Box className={classes.cardsWrapper}>
                {statusCards.map((card) => {
                    const cor = getColorForStatus(card.status);
                    return (
                        <Box key={card.status}>
                            <Box
                                className={classes.statusCard}
                                onClick={() => this.filtrarStatus(card.status)}
                                style={{ borderLeft: `8px solid ${cor}` }}
                            >
                                <Typography className={classes.statusCounter}>
                                    {this.getCounter(card.status)}
                                </Typography>
                                <Typography variant="body2" className={classes.statusBadge} style={{ backgroundColor: cor }}>
                                    {(card.label || card.status).split('\n').map((line, i, arr) => (
                                        <span key={`${card.status}-${i}`}>
                                            {line}
                                            {i < arr.length - 1 && <br />}
                                        </span>
                                    ))}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    }

    render() {
        const { classes, currentLanguage } = this.props;
        const { error, clientsModalOpen, searchTerm } = this.state;

        return (
            <AdminLayout excludeMargin>
                <Box className={classes.root}>
                    <Container>
                        {error && (
                            <Box className={classes.errorContainer}>
                                <Typography>{error}</Typography>
                            </Box>
                        )}

                        <Box className={classes.buttonGroup}>
                            <CustomButton
                                variant="greenFilled"
                                onClick={() => this.setState({ registerClientModalOpen: true })}
                            >
                                {currentLanguage.bepiService.admin.admin_home_btn_register_client || 'Cadastrar Cliente'}
                            </CustomButton>
                            <Box className={classes.searchInputWrapper}>
                                <TextField
                                    placeholder={currentLanguage.bepiService.admin.admin_home_search_cnpj_nota_placeholder}
                                    value={searchTerm}
                                    onChange={this.handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    className={classes.searchField}
                                />
                            </Box>
                        </Box>

                        <Card className={classes.panelCard}>
                            <Typography className={classes.panelTitle}>
                                {currentLanguage.bepiService.admin.admin_home_panel_title_phases}
                            </Typography>
                            {this.renderStatusCardsSafe()}
                            <Box className={classes.panelDescription}>
                                <Typography className={classes.descriptionText}>
                                    {currentLanguage.bepiService.admin.admin_home_panel_label_responsible}
                                </Typography>
                                <Box component="span" className={classes.descriptionBadge} style={{ backgroundColor: '#04476c' }}>
                                    {currentLanguage.bepiService.admin.admin_home_panel_label_admin}
                                </Box>
                                <Box component="span" className={classes.descriptionBadge} style={{ backgroundColor: '#f8792a' }}>
                                    {currentLanguage.bepiService.admin.admin_home_panel_label_client}
                                </Box>
                                <Box component="span" className={classes.descriptionBadge} style={{ backgroundColor: '#046c2c' }}>
                                    {currentLanguage.bepiService.admin.admin_home_panel_label_workshop}
                                </Box>
                            </Box>
                        </Card>

                        {this.renderTabela()}
                    </Container>
                </Box>

                <ClientsModal
                    open={clientsModalOpen}
                    onClose={() => this.setState((prev) => ({ clientsModalOpen: !prev.clientsModalOpen }))}
                    currentLanguage={currentLanguage}
                />

                <QuickCompanyModal
                    mode="new"
                    title={currentLanguage.bepiService.admin.admin_home_btn_register_client || 'Cadastrar Cliente'}
                    open={this.state.registerClientModalOpen}
                    onClose={() => this.setState({ registerClientModalOpen: false })}
                    currentLanguage={currentLanguage}
                    enqueueSnackbar={this.props.enqueueSnackbar}
                />
            </AdminLayout>
        );
    }
}

export default withRouter(withSnackbar(withStyles(styles)(AdminHomePage)));
